import { Controller } from "@hotwired/stimulus"
import JsonFetch from "#lib/json_fetch"

// The PLAY-STATE controller — sole owner + sole writer of the character's `state` blob
// (at-the-table play-state: chip/armor active, later armor damage). It is NOT a
// Collection (it has no items of its own); it composes the equipment collections via
// outlets and coordinates their play-state. See docs/cp2020-show-layer.md §4–§6.
//
// Two directions, each sourced where the data's OWNER is:
//   LOAD (push):  this controller fetches `state`, then PUSHES the active-lists onto the
//                 collections (applyActive) — it owns the persisted active-state, so it
//                 drives the paint. Fires on its own readiness AND on a collection's
//                 `chips-ready`, so it works regardless of which side is ready first.
//   SAVE (pull):  on `play-state-changed` (a user toggled an item), it PULLS each
//                 collection's `activeKeys()` (the collection owns its current toggles),
//                 rebuilds the `state` blob, and PATCHes it. It is the ONLY `state` writer.
//
// `state` shape (active-only so far). Each area chooses its shape — the area's collection
// owns the read/write of its own namespace:
//   chips: [slug,…]                                    — flat active-list (membership only)
//   armor: { "<id>": { id, active, …later: damage } }  — per-piece object (membership +,
//                                                         later, condition). Active-list is
//                                                         DERIVED from it, never stored.
export default class extends Controller {
  static values = { character: String }
  // The equipment collections whose play-state we own. They join here as they gain
  // play-state (chips pilot; armor Phase 1).
  static outlets = ["cp2020--chips", "cp2020--armor"]

  connect() {
    this._loaded = this.load() // the persisted state, fetched once
  }

  // Fetch `state` and push it onto whatever collections are already present. Idempotent.
  async load() {
    const { data } = await JsonFetch.get(`/characters/${this.characterValue}/state`)
    this._state = data && typeof data === "object" ? data : {}
    this.pushAll()
  }

  // A collection announced it finished rendering its items. Push (once our state is
  // loaded) so a collection that became ready AFTER our load still gets painted. Both the
  // outlet-connect and the *-ready event funnel here (either-order safe).
  cp2020ChipsOutletConnected() { this.pushWhenLoaded() }
  cp2020ArmorOutletConnected() { this.pushWhenLoaded() }
  chipsReady() { this.pushWhenLoaded() } // via data-action on cp2020:chips-ready
  armorReady() { this.pushWhenLoaded() } // via data-action on cp2020:armor-ready

  async pushWhenLoaded() {
    await this._loaded
    this.pushAll()
  }

  // PUSH-ON-LOAD: hand each collection its own persisted namespace. Idempotent — safe on
  // load, outlet-connect, and *-ready. Each collection announces its OWN change after being
  // painted (applyActive dispatches active-applied), so consumers that derive from active
  // state hear it from the authority — not from this controller reaching across.
  pushAll() {
    if (this.hasCp2020ChipsOutlet) this.cp2020ChipsOutlet.applyActive(this._state.chips || [])
    if (this.hasCp2020ArmorOutlet) this.cp2020ArmorOutlet.applyActive(this._state.armor || {})
  }

  // SAVE (pull): a user toggled an item's active state. Update the namespaces THIS
  // controller owns (an outlet is present) from each collection's authoritative current
  // state, and PATCH the whole `state`. MERGE over the loaded state — don't rebuild from
  // {} — so namespaces we don't (yet) own survive. Wired: cp2020:play-state-changed.
  async persist() {
    // Wait for the initial load before merging: the Worn/active checkboxes live OUTSIDE the
    // disabled fieldset, so a user can toggle before load() resolves — at which point _state
    // is undefined and the collections haven't been painted from server state yet. Awaiting
    // guarantees _state holds the persisted blob (so the merge preserves unowned namespaces)
    // and applyActive has painted every box (so the census reflects true state, not a pre-
    // load default). load() is fetched once in connect(); this is a no-op after the first.
    await this._loaded
    const state = { ...this._state }
    if (this.hasCp2020ChipsOutlet) state.chips = this.cp2020ChipsOutlet.activeKeys()
    if (this.hasCp2020ArmorOutlet) state.armor = this.cp2020ArmorOutlet.activeState()
    this._state = state
    await JsonFetch.patch(`/characters/${this.characterValue}/state`, state)
  }
}
